import React, { useState } from "react";

import { Box } from "@chakra-ui/react";
// import { useQueryClient } from "@tanstack/react-query";
import { IApiErrorData } from "api/axios/types";
import {
  IApproveAddressVerificationPayload,
  IApproveAddressVerificationResponse,
  IRejectAddressVerificationPayload,
  IRejectAddressVerificationResponse,
} from "api/compliance/types";
import { useIndividualCompliance } from "contexts/individual-compliance-context";
import { IndividualComplianceQueryKeys } from "contexts/individual-compliance-context/consts";
import { ApproveIndividualSectionModal } from "features/individual-compliance/selected-individual/ApproveIndividualSectionModal";
import { useMutateData } from "hooks/useMutateData";
import { START_INDEX } from "utils/constants";

import { customErrorToast, customSuccessToast } from "ui/custom-toast";
import { FALL_BACK_IMAGE_URL, ImageViewer } from "ui/image-viewer";
import { PdfViewer } from "ui/pdf-viewer";
import { Paragraph3 } from "ui/typography";

import { IndividualContentItem } from "./IndividualContentItem";
import { IndividualSectionAction } from "./IndividualSectionAction";
import { RejectIndividualSectionModal } from "./RejectIndividualSectionModal";
import { IIndividualContentItem, IndividualDetailsProps } from "./types";

export const AddressVerification = (props: IndividualDetailsProps) => {
  const {
    isContentWrapperBelow600,
    isContentWrapperBelow745,
    individual,
    hideApprovalButton,
  } = props;
  const {
    // selectedIndividualDetails,
    setSelectedIndividualDetails,
    rejectReason,
  } = useIndividualCompliance();
  const [isApprovalModalOpen, setApprovalModalOpen] = useState(false); // Default to true for testing
  const [isRejectionModalOpen, setRejectionModalOpen] = useState(false);
  const {
    address,
    kycDocumentType,
    state,
    lga,
    firstName,
    lastName,
    kycAddressVerificationFile,
    kycProofOfAddressType,
    addressKycStatus,
  } = individual;

  const [showIdDocument, setShowIdDocument] = useState(false);
  const {
    mutateAsync: approveAddressVerification,
    isPending: isApprovingAddressVerification,
  } = useMutateData<
    IApproveAddressVerificationPayload,
    IApproveAddressVerificationResponse
  >([IndividualComplianceQueryKeys.APPROVE_ADDRESS_VERIFICATION], {
    onSuccess: async (data) => {
      customSuccessToast({
        title: "Approve Address Verification",
        message: data.message,
      });
      // await handleFetchIndividualDetails(individual?.id);
      setSelectedIndividualDetails((prevSelectedIndividualDetails) =>
        prevSelectedIndividualDetails
          ? {
              ...prevSelectedIndividualDetails,
              addressKycStatus: "approved",
            }
          : null
      );
    },
    onError(error) {
      const err = error as unknown as IApiErrorData;
      const message = err.message;
      customErrorToast({ title: "Approve Address Verification", message });
    },
  });

  const handleApproveIndividualSection = async () => {
    try {
      await approveAddressVerification({
        apiRoute: `/control/personal/compliance/approve-address-verification`,
        data: {
          userId: individual.id,
        },
        method: "PATCH",
        module: "EARNIPAY_API",
        useEncryption: true,
      });
    } catch (error) {
      console.error(error);
    }
    setApprovalModalOpen(false);
  };

  const {
    mutateAsync: rejectAddressVerification,
    isPending: isRejectingAddressVerification,
  } = useMutateData<
    IRejectAddressVerificationPayload,
    IRejectAddressVerificationResponse
  >([IndividualComplianceQueryKeys.REJECT_ADDRESS_VERIFICATION], {
    onSuccess: async (data) => {
      customSuccessToast({
        title: "Reject Address Verification",
        message: data.message,
      });
      setSelectedIndividualDetails((previousSelectedIndividualDetails) =>
        previousSelectedIndividualDetails
          ? {
              ...previousSelectedIndividualDetails,
              addressKycStatus: "rejected",
              adminKycComment: [
                {
                  documentName: "Address Verification",
                  status: "rejected",
                  comment: rejectReason,
                  commentBy: "",
                  adminName: "",
                  timestamp: new Date().toISOString(),
                  kycJobId: previousSelectedIndividualDetails.kycJobId,
                  kycResultText:
                    previousSelectedIndividualDetails.kycResultText,
                },
                ...previousSelectedIndividualDetails.adminKycComment,
              ],
            }
          : null
      );
    },
    onError(error) {
      const err = error as unknown as IApiErrorData;
      const message = err.message;
      customErrorToast({ title: "Reject Address Verification", message });
    },
  });

  const handleRejectIndividualSection = async (reason: string) => {
    try {
      await rejectAddressVerification({
        apiRoute: `/control/personal/compliance/reject-address-verification`,
        data: {
          userId: individual.id,
          comment: reason,
        },
        method: "PATCH",
        module: "EARNIPAY_API",
        useEncryption: true,
      });
    } catch (error) {
      console.error(error);
    }
    setRejectionModalOpen(false);
  };

  const handleOpenIdDocument = () => {
    setShowIdDocument(true);
  };

  const handleCloseIdDocument = () => {
    setShowIdDocument(false);
  };

  const addressDetails: IIndividualContentItem[] = [
    {
      title: "Address",
      content: address || "N/A",
    },
    {
      title: "Document Type",
      content: (
        <Box className="flex items-center uppercase">
          <p>
            {kycProofOfAddressType?.toUpperCase() || kycDocumentType || "N/A"}
          </p>
        </Box>
      ),
    },
    {
      title: "State",
      content: state || "N/A",
    },
    {
      title: "Local Government Area",
      content: lga || "N/A",
    },
    {
      title: "Proof of Address",
      content: kycAddressVerificationFile ? (
        <Box className="mt-2">
          {kycAddressVerificationFile?.endsWith(".pdf") ? (
            <PdfViewer
              pdfUrl={kycAddressVerificationFile || ""}
              isContentWrapperBelow745={isContentWrapperBelow745}
              isContentWrapperBelow600={isContentWrapperBelow600}
            />
          ) : (
            <Box
              className={`border-[#E4E4E4] border-[1px] rounded-[7.43px] ${isContentWrapperBelow600 ? "w-fit h-[235px]" : "h-[203.36px] w-[230px]"}`}
            >
              <Box
                css={{
                  position: "relative",
                  width: "100%",

                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#00000045",
                    zIndex: 1,
                    borderTopLeftRadius: "7.43px",
                    borderTopRightRadius: "7.43px",
                  },
                }}
              >
                <img
                  src={kycAddressVerificationFile || FALL_BACK_IMAGE_URL}
                  alt={
                    kycDocumentType
                      ? `${kycDocumentType}'s document image`
                      : "document image"
                  }
                  className="object-cover h-[132.79px] w-full rounded-t-[7.43px]"
                />
              </Box>
              <Box
                className={`flex gap-[11.14px] items-center px-[15px] h-[calc(100%-132.79px)] ${isContentWrapperBelow600 || isContentWrapperBelow745 ? "justify-center" : ""}`}
              >
                <span
                  onClick={handleOpenIdDocument}
                  className="text-[#00535C] text-sm leading-[22.29px] font-medium h-[41.57px] w-[109.57px] rounded-[7.43px] py-[9.29px] px-[16.71px] border-[1px] border-[#D0D5DD] text-center cursor-pointer"
                >
                  View
                </span>
                <a
                  href={kycAddressVerificationFile || "#"}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00535C] text-sm leading-[22.29px] font-medium h-[41.57px] w-[109.57px] rounded-[7.43px] py-[9.29px] px-[16.71px] border-[1px] border-[#D0D5DD] text-center"
                >
                  Download
                </a>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        "N/A"
      ),
    },
  ];

  return (
    <>
      {individual.id ? (
        <Box className="flex flex-col gap-6">
          <Box>
            <Box
              css={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: "32px",
                rowGap: "16px",
              }}
            >
              {addressDetails.map((address, index) => (
                <IndividualContentItem
                  key={index}
                  title={address.title}
                  content={address.content}
                  isContentWrapperBelow745={isContentWrapperBelow745}
                  isContentWrapperBelow600={isContentWrapperBelow600}
                />
              ))}
            </Box>
          </Box>
          <ImageViewer
            imageDataList={[
              {
                title: "ID Document",
                imageUrl: kycAddressVerificationFile || "",
                // "https://private-user-images.githubusercontent.com/49154561/399951065-2c4e8af5-f656-4081-8096-082d10a509eb.png?jwt=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJnaXRodWIuY29tIiwiYXVkIjoicmF3LmdpdGh1YnVzZXJjb250ZW50LmNvbSIsImtleSI6ImtleTUiLCJleHAiOjE3MzYxNjQ1OTIsIm5iZiI6MTczNjE2NDI5MiwicGF0aCI6Ii80OTE1NDU2MS8zOTk5NTEwNjUtMmM0ZThhZjUtZjY1Ni00MDgxLTgwOTYtMDgyZDEwYTUwOWViLnBuZz9YLUFtei1BbGdvcml0aG09QVdTNC1ITUFDLVNIQTI1NiZYLUFtei1DcmVkZW50aWFsPUFLSUFWQ09EWUxTQTUzUFFLNFpBJTJGMjAyNTAxMDYlMkZ1cy1lYXN0LTElMkZzMyUyRmF3czRfcmVxdWVzdCZYLUFtei1EYXRlPTIwMjUwMTA2VDExNTEzMlomWC1BbXotRXhwaXJlcz0zMDAmWC1BbXotU2lnbmF0dXJlPWFjZjAyMTdiYjhkOGY3MDI5MGZhYjJmNTQyNjc0NjA4MzA1MDdhZmU5ODJkYjUzOTkzMGZjNzMzYjdiZDM0MGQmWC1BbXotU2lnbmVkSGVhZGVycz1ob3N0In0.HcokYaw9_eiuoSxTvk3hGGJVaTtCfsfC2CCrBOOAe5c",
              },
            ]}
            isVisible={showIdDocument}
            imageIndex={START_INDEX}
            handleCloseImageViewer={handleCloseIdDocument}
            uploaderName={`${firstName} ${lastName}`}
          />
        </Box>
      ) : (
        <Box>
          <Paragraph3
            lineHeight="22.4px"
            textColor="#718096"
          >
            Nothing to see here yet.
          </Paragraph3>
        </Box>
      )}
      {individual.id ? (
        <>
          {!hideApprovalButton && (
            <IndividualSectionAction
              isRejectSectionDisabled={addressKycStatus !== "pending"} // Placeholder default state
              isApproveSectionDisabled={addressKycStatus !== "pending"} // Placeholder default state
              handleAddRejectionReason={() => setRejectionModalOpen(true)} // Placeholder function
              handleApproveSection={() => setApprovalModalOpen(true)} // Placeholder function
              isContentWrapperBelow450={false} // Placeholder default state
            />
          )}
          <>
            <ApproveIndividualSectionModal
              isOpen={isApprovalModalOpen}
              setIsOpen={setApprovalModalOpen}
              handleToggleApprovalSectionModal={() =>
                setApprovalModalOpen(!isApprovalModalOpen)
              }
              handleApproveIndividualSection={handleApproveIndividualSection}
              isLoading={isApprovingAddressVerification}
            />

            <RejectIndividualSectionModal
              isOpen={isRejectionModalOpen}
              setIsOpen={setRejectionModalOpen}
              handleToggleRejectSectionModal={() =>
                setRejectionModalOpen(!isRejectionModalOpen)
              }
              handleRejectIndividualSection={handleRejectIndividualSection}
              isLoading={isRejectingAddressVerification}
            />
          </>
        </>
      ) : null}
    </>
  );
};
